import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import { MobilePhoneIcon, XIcon, ExternalLinkIcon, PlusIcon } from "./Icons";

export default function InstallPwaModal() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // 1. Detect if running inside installed standalone mode
    const standaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standaloneMode);

    if (standaloneMode) {
      return; // Already installed and running as standalone app
    }

    // 2. Detect iOS devices
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // 3. Listen for Android / Chrome / Desktop PWA install prompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Check if user dismissed prompt recently
      const dismissed = localStorage.getItem("vendor_pwa_dismissed");
      const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
      const hoursSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60);

      // Show after 24 hours of dismissal or if never dismissed
      if (!dismissed || hoursSinceDismiss > 24) {
        setShowPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // 4. For iOS devices, show banner if not dismissed recently
    if (isIosDevice) {
      const dismissed = localStorage.getItem("vendor_pwa_dismissed");
      const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
      const hoursSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (!dismissed || hoursSinceDismiss > 24) {
        // Delay slightly for smooth page load
        const timer = setTimeout(() => setShowPrompt(true), 2500);
        return () => clearTimeout(timer);
      }
    }

    // 5. Expose global trigger for manual install button in Sidebar/Navbar
    window.triggerPwaInstall = () => {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choice) => {
          if (choice.outcome === "accepted") {
            setShowPrompt(false);
          }
          setDeferredPrompt(null);
        });
      } else if (isIosDevice) {
        setShowIosGuide(true);
      } else {
        alert("To install this app on your phone, open your browser menu (⋮) and tap 'Add to Home screen' or 'Install app'.");
      }
    };

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, [deferredPrompt]);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          setShowPrompt(false);
          localStorage.setItem("vendor_pwa_installed", "true");
        }
        setDeferredPrompt(null);
      });
    } else if (isIOS) {
      setShowIosGuide(true);
    } else {
      setShowPrompt(false);
      alert("Tap your browser menu (three dots at top right) and select 'Add to Home Screen' or 'Install App'.");
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIosGuide(false);
    localStorage.setItem("vendor_pwa_dismissed", Date.now().toString());
  };

  // Do not render anything if already installed as standalone
  if (isStandalone) {
    return null;
  }

  return (
    <>
      {/* ── FLOATING APP INSTALL BANNER (SELLER'S CHOICE) ── */}
      {showPrompt && !showIosGuide && (
        <aside
          aria-label="Add to Home Screen Prompt"
          className="pwa-install-banner"
          style={{
            position: "fixed",
            bottom: "16px",
            left: "16px",
            right: "16px",
            maxWidth: "460px",
            margin: "0 auto",
            background: "#ffffff",
            border: "1.5px solid #edf0f4",
            borderRadius: "16px",
            padding: "16px 18px",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.16)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "14px",
            animation: "pwaSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards"
          }}
        >
          {/* App Icon & Details */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "#ffffff",
                border: "1.5px solid #faedea",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)"
              }}
            >
              <img src={logo} alt="IM Online" style={{ height: "26px", objectFit: "contain" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <strong style={{ fontSize: "13.5px", color: "#0f172a", whiteSpace: "nowrap" }}>
                  IM Online Seller App
                </strong>
                <span style={{ fontSize: "9.5px", fontWeight: "800", color: "#166534", background: "#dcfce7", padding: "1px 5px", borderRadius: "4px" }}>
                  FREE
                </span>
              </div>
              <p style={{ fontSize: "11.5px", color: "#64748b", margin: "2px 0 0", lineHeight: "1.3" }}>
                Add to your home screen for 1-tap dashboard access without logging in every time.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleInstallClick}
              className="btn btn-primary"
              style={{
                padding: "8px 14px",
                fontSize: "12.5px",
                fontWeight: "800",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <MobilePhoneIcon size={14} color="#ffffff" />
              <span>Add App</span>
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss install banner"
              style={{
                background: "#f1f5f9",
                border: "none",
                borderRadius: "8px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                cursor: "pointer"
              }}
            >
              <XIcon size={15} color="#64748b" />
            </button>
          </div>
        </aside>
      )}

      {/* ── IOS STEP-BY-STEP INSTALL GUIDE MODAL ── */}
      {showIosGuide && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 10000,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: "16px"
          }}
          onClick={handleDismiss}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              padding: "24px 22px",
              maxWidth: "420px",
              width: "100%",
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              animation: "pwaSlideUp 0.3s ease-out"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <img src={logo} alt="IM Online" style={{ height: "30px", objectFit: "contain" }} />
                <strong style={{ fontSize: "16px", color: "#0f172a" }}>Install on iPhone / iPad</strong>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                style={{ background: "#f1f5f9", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", display: "grid", placeItems: "center" }}
              >
                <XIcon size={14} color="#64748b" />
              </button>
            </div>

            <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "18px" }}>
              Follow these 2 quick steps to add the IM Online Seller Center app directly to your iPhone home screen:
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "22px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#f8fafc", padding: "12px 14px", borderRadius: "12px", border: "1px solid #edf0f4" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "rgba(168, 80, 80, 0.08)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <ExternalLinkIcon size={18} color="#a85050" />
                </div>
                <span style={{ fontSize: "13px", color: "#1e293b" }}>
                  1. Tap the <strong>Share button</strong> at the bottom of your Safari screen.
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#f8fafc", padding: "12px 14px", borderRadius: "12px", border: "1px solid #edf0f4" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "rgba(168, 80, 80, 0.08)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <PlusIcon size={18} color="#a85050" />
                </div>
                <span style={{ fontSize: "13px", color: "#1e293b" }}>
                  2. Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>, then tap <strong>&ldquo;Add&rdquo;</strong>.
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              className="btn btn-primary w-100"
              style={{ padding: "10px", fontSize: "14px", fontWeight: "800" }}
            >
              Got It, Thanks!
            </button>
          </div>
        </div>
      )}
    </>
  );
}
